import type { ReactNode } from "react";

import { Badge } from "@/shared/ui/badge";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { cn } from "@/shared/lib/utils";

import type { SiteBlock, SiteContent } from "../../api/types";
import { blockLabel, getAt } from "../../model/content";
import { optionLabel } from "../../model/schemaLabels";
import { previewHost } from "../../model/siteLabels";

export type PreviewDevice = "desktop" | "mobile";

/**
 * 屏 4 右栏「草稿预览 · 实时」:浏览器框头 + 顶导航 + 当前页各区块的**简版渲染**(足够看文案,不是真主题)。
 * ★ 每个字都来自 site.json:标题 / 副标 / 按钮 / 产品名 / 案例 / 资质数 / 表单字段 / 电话地址;
 *   没有的就不画(或画「暂无动态」这种状态词),不替客户编一句宣传语。
 * ★ 当前区块外描边 + 角标「正在编辑 · {区块}」;电脑 / 手机只改容器宽(100% / 390px)。
 * ★ 预览里的「顶导航」只是画面,不是本页的导航:画成普通 div,不占 `nav` 地标(读屏的地标列表里
 *   不该多出一个点不动的「站点导航」)。
 * ★ site.json 是用户可编辑 / ai 产出的开放结构:列表元素可能是 null / 字符串,画之前先筛成对象。
 * ★ 区块 props 只有模板 schema 声明过的键(附录 A.32,ai 初稿按 schema 收形):内容来自根级数组的区块(产品 / 案例 /
 *   资质 / 新闻 / 表单 / 页脚)props 恒 `{}`,核心优势只有 `items`,首屏只有 `title / subtitle / cta / image`。
 *   所以区块小标题没有就用区块名兜底(「产品精选」),不读 schema 外的键(`secondaryCta` / `limit` 这类 api 会剥掉)。
 */
export function SitePreview({
  content,
  previewUrl,
  pageSlug,
  activeBlockId,
  device,
}: {
  content: SiteContent;
  previewUrl: string;
  pageSlug: string;
  activeBlockId: string | null;
  device: PreviewDevice;
}) {
  const page = content.pages.find((p) => p.slug === pageSlug) ?? content.pages[0];
  return (
    <section
      aria-label="草稿预览"
      className={cn("border border-border bg-muted/30 p-3", CARD_WALL_RADIUS)}
    >
      <div className="mx-auto" style={{ width: device === "mobile" ? 390 : "100%" }}>
        <div className="flex items-center gap-2 rounded-t-md border border-border bg-card px-3 py-1.5">
          <span aria-hidden className="flex gap-1">
            <i className="h-2 w-2 rounded-full bg-destructive/70" />
            <i className="h-2 w-2 rounded-full bg-warning/70" />
            <i className="h-2 w-2 rounded-full bg-success/70" />
          </span>
          <span className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-0.5 font-mono text-2xs text-muted-foreground">
            {previewHost(previewUrl)}/?preview=draft
          </span>
          <Badge variant="outline" className="shrink-0 normal-case tracking-normal">
            草稿预览 · 实时
          </Badge>
        </div>
        <div
          data-testid="preview-frame"
          data-device={device}
          style={{ width: device === "mobile" ? 390 : "100%" }}
          className="max-w-full overflow-hidden rounded-b-md border border-t-0 border-border bg-white text-neutral-900"
        >
          <div
            data-testid="preview-nav"
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-neutral-200 px-5 py-2.5 text-xs"
          >
            <span className="font-semibold">{content.brand.name}</span>
            <ul className="flex flex-wrap gap-3 text-neutral-600">
              {content.pages.map((p) => (
                <li
                  key={p.slug}
                  className={cn(p.slug === page?.slug && "font-semibold text-neutral-900")}
                >
                  {p.title}
                </li>
              ))}
            </ul>
            <span className="font-semibold">{content.brand.phone}</span>
          </div>
          {page?.blocks.map((b) => (
            <BlockView key={b.id} block={b} content={content} active={b.id === activeBlockId} />
          ))}
        </div>
      </div>
    </section>
  );
}

const str = (v: unknown): string =>
  typeof v === "string" ? v : typeof v === "number" ? String(v) : "";

type Row = Record<string, unknown>;

/** 开放结构的列表 → 只留对象元素(null / 字符串 / 数字这类坏元素不画,也不让预览崩)。 */
function rows(v: unknown): Row[] {
  return Array.isArray(v)
    ? v.filter((x): x is Row => x != null && typeof x === "object" && !Array.isArray(x))
    : [];
}

function BlockView({
  block,
  content,
  active,
}: {
  block: SiteBlock;
  content: SiteContent;
  active: boolean;
}) {
  const p = block.props;
  // 首屏的 title 是正文(主标题),没有就不画;别的区块的小标题 schema 里没有,缺了用区块名兜底。
  const title = str(p.title) || (block.type === "hero_split" ? "" : blockLabel(block.type));
  let body: ReactNode;
  switch (block.type) {
    case "hero_split": {
      const cta = str(getAt(p, "cta.label"));
      body = (
        <div className="space-y-3 py-4">
          {title && <h2 className="text-2xl font-bold leading-tight">{title}</h2>}
          {str(p.subtitle) && (
            <p className="max-w-prose text-sm text-neutral-600">{str(p.subtitle)}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {cta && <Pill primary>{cta}</Pill>}
            {content.brand.phone && (
              <span className="ml-1 font-semibold text-neutral-700">{content.brand.phone}</span>
            )}
          </div>
        </div>
      );
      break;
    }
    case "features_3": {
      const items = rows(p.items);
      body = (
        <div className="space-y-3">
          {title && <h3 className="text-base font-semibold">{title}</h3>}
          {items.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-3">
              {items.map((it, i) => (
                <div key={i} className="rounded-md border border-neutral-200 p-3">
                  {str(it.title) && <div className="text-sm font-semibold">{str(it.title)}</div>}
                  {str(it.desc) && <p className="mt-1 text-xs text-neutral-600">{str(it.desc)}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      );
      break;
    }
    case "product_grid": {
      const products = rows(content.products);
      body = (
        <div className="space-y-3">
          {title && <h3 className="text-base font-semibold">{title}</h3>}
          {products.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-3">
              {products.map((it, i) => (
                <div key={i} className="rounded-md border border-neutral-200 p-3">
                  <div className="mb-2 h-16 rounded bg-neutral-100" aria-hidden />
                  {str(it.name) && <div className="text-sm font-semibold">{str(it.name)}</div>}
                  {str(it.summary) && (
                    <p className="mt-0.5 text-xs text-neutral-600">{str(it.summary)}</p>
                  )}
                  {str(it.priceMode) && (
                    <div className="mt-1.5 text-xs font-medium text-primary">
                      <span>{optionLabel(str(it.priceMode))}</span> →
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      );
      break;
    }
    case "case_wall": {
      const cases = rows(content.cases);
      body = (
        <div className="space-y-3">
          {title && <h3 className="text-base font-semibold">{title}</h3>}
          {cases.length > 0 ? (
            <div className="grid gap-2 sm:grid-cols-3">
              {cases.map((c, i) => (
                <div key={i} className="rounded-md border border-neutral-200 p-3 text-xs">
                  <div className="font-semibold">{str(c.client)}</div>
                  {str(c.outcome) && <p className="mt-0.5 text-neutral-600">{str(c.outcome)}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-neutral-500">暂无公开案例</p>
          )}
        </div>
      );
      break;
    }
    case "credential_wall":
      body = (
        <div className="space-y-2">
          {title && <h3 className="text-base font-semibold">{title}</h3>}
          <p className="text-xs text-neutral-600">
            {Array.isArray(content.credentials) ? content.credentials.length : 0} 项资质
          </p>
        </div>
      );
      break;
    case "news_list": {
      const news = rows(content.news);
      body = (
        <div className="space-y-2">
          {title && <h3 className="text-base font-semibold">{title}</h3>}
          {news.length > 0 ? (
            <ul className="space-y-1 text-xs">
              {news.map((n, i) => (
                <li key={i}>{str(n.title)}</li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-neutral-500">暂无动态</p>
          )}
        </div>
      );
      break;
    }
    case "lead_form": {
      const leadFields = Array.isArray(content.settings?.leadForm?.fields)
        ? content.settings.leadForm.fields.filter((f): f is string => typeof f === "string")
        : [];
      body = (
        <div className="space-y-2">
          {title && <h3 className="text-base font-semibold">{title}</h3>}
          <div className="grid gap-2 sm:grid-cols-3">
            {leadFields.map((f) => (
              <div key={f} className="text-xs">
                <span className="text-neutral-600">{optionLabel(f)}</span>
                <div
                  className="mt-0.5 h-7 rounded border border-neutral-300 bg-white"
                  aria-hidden
                />
              </div>
            ))}
          </div>
        </div>
      );
      break;
    }
    case "contact_footer":
      body = (
        <div className="space-y-0.5 text-xs text-neutral-600">
          <div className="font-semibold text-neutral-900">{content.brand.name}</div>
          {content.brand.phone && <div>{content.brand.phone}</div>}
          {content.brand.address && <div>{content.brand.address}</div>}
          {content.brand.icpNo && <div>{content.brand.icpNo}</div>}
        </div>
      );
      break;
    default:
      body = (
        <div className="text-xs text-neutral-500">
          {blockLabel(block.type)} · <code>{block.type}</code>
        </div>
      );
  }

  return (
    <div
      data-block-id={block.id}
      className={cn(
        "relative px-5 py-4",
        active && "outline outline-2 -outline-offset-2 outline-primary"
      )}
    >
      {active && (
        <span className="absolute right-2 top-2 rounded bg-primary px-1.5 py-0.5 text-2xs font-medium text-primary-foreground">
          正在编辑 · {blockLabel(block.type)}
        </span>
      )}
      {body}
    </div>
  );
}

function Pill({ children, primary }: { children: ReactNode; primary?: boolean }) {
  return (
    <span
      className={cn(
        "rounded-md px-3 py-1.5 font-medium",
        primary
          ? "bg-primary text-primary-foreground"
          : "border border-neutral-300 text-neutral-700"
      )}
    >
      {children}
    </span>
  );
}
